import React, {useEffect, useRef} from "react";
import type {InputRef} from "antd";
import {Button, Input} from "antd";
import {AppstoreOutlined, PlusOutlined, SearchOutlined} from "@ant-design/icons";
import {AnimatePresence, LayoutGroup} from "framer-motion";
import AppEntity from "../../types/AppEntity";
import PlatformType from "../../types/PlatformType";
import {PLATFORMS, platformInfo} from "../../services/Platform";
import AppRow from "./AppRow";
import StatusIcon from "./StatusIcon";
import styles from "./Apps.module.css";

interface AppListProps {
    apps: AppEntity[]
    counts: Record<PlatformType, number>
    loading: boolean
    isAdmin: boolean
    selectedPlatform: PlatformType
    devicePlatform?: PlatformType
    search: string
    onSearch: (value: string) => void
    onSelectPlatform: (platform: PlatformType) => void
    onUpload: () => void
    onEdit: (app: AppEntity) => void
    onRelease: (app: AppEntity) => void
    onDelete: (app: AppEntity) => void
}

const stamp = (app: AppEntity) => new Date(app.publishedAt || app.createdAt).getTime() || 0;

function sortForDevice(apps: AppEntity[], device?: PlatformType) {
    return [...apps].sort((a, b) =>
        (device ? Number(b.platform === device) - Number(a.platform === device) : 0) || stamp(b) - stamp(a));
}

export default function AppList(props: AppListProps) {
    const {apps, counts, loading, isAdmin, selectedPlatform, devicePlatform, search} = props;
    const searchRef = useRef<InputRef>(null);

    useEffect(() => {
        const onKey = (event: KeyboardEvent) => {
            const target = event.target as HTMLElement;
            const typing = target.closest("input, textarea, [contenteditable=true]");
            if (event.key === "/" && !typing && !event.metaKey && !event.ctrlKey) {
                event.preventDefault();
                searchRef.current?.focus();
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);
    const query = search.trim().toLowerCase();
    const visible = sortForDevice(
        apps.filter(app => !query || app.name.toLowerCase().includes(query) || app.description?.toLowerCase().includes(query)),
        devicePlatform,
    );
    const filtered = selectedPlatform !== PlatformType.ALL;
    const showDeviceHint = !filtered && !!devicePlatform;

    const groups = isAdmin
        ? [
            {key: "draft", label: "Draft", released: false, items: visible.filter(a => !a.publishedAt)},
            {key: "released", label: "Released", released: true, items: visible.filter(a => a.publishedAt)},
        ].filter(g => g.items.length > 0)
        : [{key: "all", label: "", released: true, items: visible}];

    const rows = (items: AppEntity[]) => items.map(app => (
        <AppRow
            key={app.id}
            app={app}
            isAdmin={isAdmin}
            isThisDevice={showDeviceHint && app.platform === devicePlatform}
            onEdit={props.onEdit}
            onRelease={props.onRelease}
            onDelete={props.onDelete}
        />
    ));

    return (
        <section aria-labelledby="apps-title">
            <div className={styles.pageHead}>
                <div>
                    <h1 id="apps-title" className={styles.pageTitle}>Apps</h1>
                    <p className={styles.pageLede}>
                        {isAdmin
                            ? "Upload builds, release them to staff, and keep versions current."
                            : "Company apps for Android, Windows, macOS and Linux."}
                    </p>
                </div>
            </div>

            <div className={styles.toolbar}>
                <nav className={styles.tabs} aria-label="Filter by platform">
                    {PLATFORMS.map(platform => {
                        const selected = platform.type === selectedPlatform;
                        const isDevice = platform.type === devicePlatform;
                        return (
                            <button
                                key={platform.type}
                                type="button"
                                className={`${styles.tab} ${selected ? styles.tabSelected : ""}`}
                                aria-pressed={selected}
                                onClick={() => props.onSelectPlatform(platform.type)}
                                title={isDevice ? `${platform.name} (your device)` : platform.name}
                            >
                                {platform.type === PlatformType.ALL ? "All" : platform.name}
                                {isDevice && <span className={styles.tabDot} aria-label="your device"/>}
                                <span className={`${styles.tabCount} mono`}>{counts[platform.type]}</span>
                            </button>
                        );
                    })}
                </nav>
                <Input
                    ref={searchRef}
                    className={styles.search}
                    prefix={<SearchOutlined/>}
                    suffix={search ? undefined : <kbd className={styles.kbd} title="Press / to search">/</kbd>}
                    placeholder="Search apps"
                    allowClear
                    value={search}
                    onChange={e => props.onSearch(e.target.value)}
                    aria-label="Search apps"
                />
            </div>

            <div className={styles.list}>
                {loading && (
                    <ul className={styles.rows} aria-busy="true" aria-label="Loading apps">
                        {[0, 1, 2, 3, 4].map(i => (
                            <li key={i} className={styles.skeletonRow}>
                                <span className={styles.skeletonIcon}/>
                                <span className={styles.skeletonText}>
                                    <span style={{width: `${34 - i * 3}%`}}/>
                                    <span style={{width: `${58 - i * 5}%`}}/>
                                </span>
                            </li>
                        ))}
                    </ul>
                )}

                {!loading && visible.length === 0 && (
                    <div className={styles.empty}>
                        <span className={styles.emptyIcon}><AppstoreOutlined/></span>
                        <p className={styles.emptyTitle}>
                            {query
                                ? `No apps match “${search.trim()}”`
                                : filtered ? `No ${platformInfo(selectedPlatform).name} apps yet` : "No apps yet"}
                        </p>
                        <p className={styles.emptyText}>
                            {query
                                ? "Check the spelling or clear the search."
                                : filtered
                                    ? `Nothing has been ${isAdmin ? "uploaded" : "released"} for ${platformInfo(selectedPlatform).name}.`
                                    : isAdmin
                                        ? "Upload a build to get started. It stays a draft until you release it."
                                        : "Nothing has been released yet. Check back after the release team publishes a build."}
                        </p>
                        <div className={styles.emptyActions}>
                            {query && <Button onClick={() => props.onSearch("")}>Clear search</Button>}
                            {!query && filtered && <Button onClick={() => props.onSelectPlatform(PlatformType.ALL)}>Show all platforms</Button>}
                            {isAdmin && !query && <Button type="primary" icon={<PlusOutlined/>} onClick={props.onUpload}>Upload build</Button>}
                        </div>
                    </div>
                )}

                {!loading && visible.length > 0 && (
                    <LayoutGroup>
                        {groups.map(group => (
                            <div key={group.key} className={styles.group}>
                                {isAdmin && (
                                    <div className={styles.groupHead}>
                                        <StatusIcon released={group.released}/>
                                        <span>{group.label}</span>
                                        <span className={`${styles.groupCount} mono`}>{group.items.length}</span>
                                    </div>
                                )}
                                <ul className={styles.rows}>
                                    <AnimatePresence initial={false} mode="popLayout">
                                        {rows(group.items)}
                                    </AnimatePresence>
                                </ul>
                            </div>
                        ))}
                    </LayoutGroup>
                )}
            </div>
        </section>
    );
}
